
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Form } from 'vee-validate';
import AppSelect from '@/components/UI/AppSelect.vue';
import AppButton from '@/components/UI/AppButton.vue';
import AppCheckbox from '@/components/UI/AppCheckbox.vue';
import { useRegistrationStore } from '../store/registrationStore';
import AppLoader from '@/components/UI/AppLoader.vue';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import { HEALTHCARE, STUDY_TYPE } from '@/types/enums';

export default defineComponent({
  components: {
    Form,
    AppSelect,
    AppButton,
    AppCheckbox,
    AppLoader,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const registrationStore = useRegistrationStore();
    const mainStore = useMainStore();
    const size = ref<'default' | 'large' | 'small'>('large');

    const dataRequest = ref(true);
    const professionDetailValue = ref();
    const graduationDate = ref(registrationStore.createUser.graduationDate);
    const isStudent = ref(registrationStore.createUser.studyType === STUDY_TYPE.STUDENT);
    const isResident = ref(registrationStore.createUser.studyType === STUDY_TYPE.RESIDENT);
    const label = computed(() => route.meta?.label || 'registration.specialty.label');
    const placeholder = computed(() => route.meta?.placeholder || 'registration.specialty.placeholder');

    const professionDetailList = computed(() => mainStore.professionDetail.types || mainStore.professionDetail.professionals || []);
    const studyType = computed(() => {
      if (isStudent.value) {
        return STUDY_TYPE.STUDENT;
      }
      if (isResident.value) {
        return STUDY_TYPE.RESIDENT;
      }
      return null;
    });

    const hideResident = computed(() => {
      const { profession } = registrationStore.createUser;
      if (profession === HEALTHCARE.ALLIED_HEALTH) {
        return true;
      }
      if (profession === HEALTHCARE.OTHER) {
        if (professionDetailValue.value === 2) {
          return true;
        }
      }
      return false;
    });

    const hideStudent = computed(() => {
      const { profession } = registrationStore.createUser;
      if (profession === HEALTHCARE.OTHER) {
        if (professionDetailValue.value === 2) {
          return true;
        }
      }
      return false;
    });

    const currentNextStep = computed(() => {
      const { profession } = registrationStore.createUser;
      if (profession === HEALTHCARE.NURSING) {
        if (professionDetailValue.value === 10) {
          return 'step-5a';
        }
        return 'step-3b-1';
      }
      if (profession === HEALTHCARE.OTHER) {
        if (professionDetailValue.value === 3) {
          return 'step-5';
        }
        if (professionDetailValue.value === 2) {
          return 'step-5c';
        }
      }
      if (profession === HEALTHCARE.ALLIED_HEALTH) {
        if (professionDetailValue.value === 20) {
          return 'step-5';
        }
      }
      if (profession === HEALTHCARE.DENTAL) {
        if (professionDetailValue.value === 13) {
          return 'step-5b';
        }
      }
      if (profession === HEALTHCARE.MEDICAL) {
        if (professionDetailValue.value === 38) {
          return 'step-5b';
        }
      }
      return 'step-7';
    });

    function nextStep() {
      if (mainStore.professionDetail.types) {
        registrationStore.createUser.professionType = professionDetailValue.value;
      } else {
        registrationStore.createUser.professionSpeciality = professionDetailValue.value;
      }
      registrationStore.createUser.studyType = studyType.value;
      if (graduationDate.value && (isStudent.value || isResident.value)) {
        registrationStore.createUser.graduationDate = new Date(graduationDate.value).toISOString();
      }

      router.push(currentNextStep.value);
    }

    function getProfessionDetailData(value) {
      mainStore.fetchProfessionDetail(value)
        .then((response) => {
          if (!response.professionals && !response.types) {
            router.replace('step-7');
          }
        })
        .finally(() => { dataRequest.value = false; });
    }

    onMounted(() => {
      if (registrationStore.createUser.professionType !== undefined) {
        professionDetailValue.value = registrationStore.createUser.professionType;
      } else {
        professionDetailValue.value = registrationStore.createUser.professionSpeciality;
      }

      if (registrationStore.createUser.profession !== undefined) {
        getProfessionDetailData(registrationStore.createUser.profession);
      } else {
        router.push('step-3');
      }
      MonitoringService.sendPageMountedSpeed();
    });

    function yearsToMilliseconds(years) {
      return years * 1000 * 60 * 60 * 24 * 365;
    }

    function disabledDate(time) {
      const date = new Date();
      const dateWithoutTime = Date.parse(date.toDateString());
      return time.getTime() < dateWithoutTime || time.getTime() > (dateWithoutTime + yearsToMilliseconds(4));
    }

    return {
      nextStep,
      size,
      dataRequest,
      professionDetailValue,
      isStudent,
      isResident,
      graduationDate,
      professionDetailList,
      mainStore,
      disabledDate,
      label,
      placeholder,
      hideResident,
      hideStudent,
    };
  },
});
